import { useState, useEffect } from "react";
import {
  ArrowLeft,
  Target,
  Utensils,
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import BottomNavBar from "../../components/BottomNavBar";
import welcomeBg from "../../assets/homebg.png";
import homeService, { HomePageData } from "../../services/HomeService";
import { useHomeDataForDate } from "../../hooks/useHomeDataForDate";

const BiteBalance = () => {
  const navigate = useNavigate();

  // Fetch home page data using the date passed via navigation state (defaults to today)
  const {
    data: homeData,
    isLoading: loading,
    selectedDate,
    formattedSelectedDate,
  } = useHomeDataForDate();

  // Get score classification based on total score (out of 100)
  const getScoreClassification = (score: number) => {
    if (score >= 80) {
      return { label: "Excellent", color: "text-green-600", bgColor: "bg-green-500" };
    }
    if (score >= 60) {
      return { label: "Good", color: "text-blue-600", bgColor: "bg-blue-500" };
    }
    if (score >= 40) {
      return { label: "Fair", color: "text-yellow-600", bgColor: "bg-yellow-500" };
    }
    return { label: "Needs Improvement", color: "text-red-600", bgColor: "bg-red-500" };
  };

  const gutHealthScore = homeData?.["Food GHI"]?.daily_ghi || 0;
  const foodLabel = homeData?.["Food GHI"]?.label || "";
  const maxScore = 100;

  // Use backend value directly - NO conversion
  const displayScore = gutHealthScore;
  const classification = getScoreClassification(displayScore);
  const scorePercentage = displayScore;

  // Show loading state
  if (loading) {
    return (
      <div
        className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
        style={{
          minHeight: "100vh",
          maxHeight: "100vh",
          overflow: "hidden",
        }}
      >
        <div className="flex h-screen items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            <p className="text-footnote text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-20"
        style={{ height: "calc(100vh - 80px)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="h-10 w-10 rounded-xl border-border bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-title3 font-bold text-foreground">
            {formattedSelectedDate} Gut Health Score
          </h1>
          <div className="h-10 w-10" />
        </div>

        {/* Today's Gut Health Score Card */}
        <Card className="rounded-2xl border-0 bg-gradient-to-br from-blue-50 to-purple-50 p-6 shadow-sm dark:from-blue-950 dark:to-purple-950">
          <div className="text-center">
            <p className="mb-4 text-footnote font-medium text-muted-foreground">
              Today's Gut Health Score
            </p>
            <div className="mb-2 text-6xl font-bold text-blue-600 dark:text-blue-400">
              {Math.round(displayScore)}
            </div>
            <p className="mb-4 text-footnote text-muted-foreground">
              of 100 maximum
            </p>
            {foodLabel && (
              <div className="mb-4">
                <span className={`inline-block rounded-full px-4 py-1.5 text-footnote font-semibold ${classification.color}`}>
                  {foodLabel}
                </span>
              </div>
            )}

            {/* Progress Bar */}
            <div className="mx-auto h-3 w-full overflow-hidden rounded-full bg-blue-200 dark:bg-blue-900">
              <div
                className={`h-full transition-all duration-500 ${classification.bgColor}`}
                style={{ width: `${scorePercentage}%` }}
              />
            </div>
          </div>
        </Card>

        {/* What is Gut Health Score Card with Integrated Content */}
        <Card className="rounded-3xl border-0 bg-card p-6 shadow-sm overflow-hidden">
          <h3 className="mb-3 flex items-center gap-2 text-headline font-bold text-[#1a365d]">
            <Utensils className="h-5 w-5 text-blue-500" />
            What is Gut Health Score?
          </h3>
          <p className="text-callout leading-relaxed text-muted-foreground mb-6">
            Gut Health Score measures the quality of your dietary intake on a scale of 0-100. It evaluates nutrient density, the balance of macronutrients, and gut-friendly properties of your meals. A higher score indicates better nutritional choices that support digestive health and overall wellbeing.
          </p>

          {/* Integrated Calculated From Selection */}
          <div className="rounded-2xl bg-[#f8fafc] p-5">
            <h4 className="mb-4 text-caption1 font-bold text-[#475569] uppercase tracking-wider">
              Calculated From
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-callout">
                <span className="text-blue-500 mt-1 flex-shrink-0">•</span>
                <p className="text-muted-foreground">
                  <span className="font-semibold text-foreground">Carb-Fiber Balance:</span> Ratio of carbs to fiber intake
                </p>
              </li>
              <li className="flex items-start gap-2 text-callout">
                <span className="text-blue-500 mt-1 flex-shrink-0">•</span>
                <p className="text-muted-foreground">
                  <span className="font-semibold text-foreground">Fat Quality:</span> Saturated, trans, and total fat levels
                </p>
              </li>
              <li className="flex items-start gap-2 text-callout">
                <span className="text-blue-500 mt-1 flex-shrink-0">•</span>
                <p className="text-muted-foreground">
                  <span className="font-semibold text-foreground">Sugar Load:</span> Sugar intake relative to total carbs
                </p>
              </li>
              <li className="flex items-start gap-2 text-callout">
                <span className="text-blue-500 mt-1 flex-shrink-0">•</span>
                <p className="text-muted-foreground">
                  <span className="font-semibold text-foreground">Meal Scores:</span> Individual meal quality ratings
                </p>
              </li>
            </ul>
          </div>
        </Card>

        {/* Score Ranges */}
        <Card className="rounded-3xl border-0 bg-card p-6 shadow-sm">
          <h3 className="mb-4 text-headline font-bold text-foreground">
            Score Ranges
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-border/40 pb-2">
              <span className="text-subhead font-medium text-foreground">
                Excellent
              </span>
              <span className="text-subhead font-semibold text-blue-500">
                80-100
              </span>
            </div>
            <div className="flex items-center justify-between border-b border-border/40 pb-2">
              <span className="text-subhead font-medium text-foreground">Good</span>
              <span className="text-subhead font-semibold text-blue-500">60-79</span>
            </div>
            <div className="flex items-center justify-between border-b border-border/40 pb-2">
              <span className="text-subhead font-medium text-foreground">Fair</span>
              <span className="text-subhead font-semibold text-blue-500">
                40-59
              </span>
            </div>
            <div className="flex items-center justify-between pt-1">
              <span className="text-subhead font-medium text-foreground">
                Needs Improvement
              </span>
              <span className="text-subhead font-semibold text-red-500">0-39</span>
            </div>
          </div>
        </Card>

        {/* Ways to Improve */}
        <Card className="rounded-2xl border-0 bg-card p-6 shadow-sm">
          <h3 className="mb-4 text-headline font-bold text-foreground">
            Ways to Improve
          </h3>
          <ul className="space-y-3 text-callout text-muted-foreground">
            <li className="flex items-start gap-3">
              <span className="mt-0.5 text-blue-500">•</span>
              <span>Increase variety of colorful vegetables in your meals</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 text-blue-500">•</span>
              <span>Choose whole grains over refined carbohydrates</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 text-blue-500">•</span>
              <span>Include more fermented foods for gut health</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 text-blue-500">•</span>
              <span>Prioritize omega-3 rich foods like fish and nuts</span>
            </li>
          </ul>
        </Card>
      </div>

      <BottomNavBar activeIndex={0} />
    </div>
  );
};

export default BiteBalance;
